// src/views/Delivery/DeliveryOptionForm.js
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { MdRemoveCircle } from "react-icons/md";
import deliveryApi from "../../services/deliveryapi";
import { showConfirm } from "react-confirm-prompt";

const ModalContainer = styled(Modal)`
  .modal-dialog { max-width: 90%; }
  .modal-header, .modal-footer { background-color: #f8f9fa; border: none; }
  .modal-title { font-weight: 600; }
  .modern-input {
    border-radius: 10px; border: 1px solid #ccc;
    box-shadow: 0 1px 3px rgba(0,0,0,.1); padding: 10px;
  }
  .save-btn, .cancel-btn { border-radius: 20px; font-weight: 600; min-width: 110px; }
  .save-btn { background-color: #28a745; color: white; }
  .cancel-btn { background-color: #6c757d; color: white; }

  .fee-entry {
    position: relative; background-color: #f1f3f5; border-radius: 8px;
    padding: 18px 18px 12px; box-shadow: 0 2px 5px rgba(0,0,0,.1);
    margin-bottom: 14px;
  }
  .remove-icon {
    position: absolute; right: 10px; top: 10px;
    font-size: 22px; cursor: pointer; color: #dc3545;
  }
  .remove-icon.disabled { opacity: .4; pointer-events: none; }
  .hint { font-size: 12px; opacity: .8; }
`;

const DEFAULT_FEE_ROW = () => ({
  min_order_value: "",
  max_order_value: "",
  min_radius_km: "0",
  max_radius_km: "",
  fee: "",
  smart_cart_fee: "",
  threshold: "",
  status: 1,
});

const normalizeNumberString = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return String(v);
  return String(v).trim();
};

const toInt = (v, fallback = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};

const toFloat = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const DeliveryOptionForm = ({ show, onHide, initialData, onSave, allBranches = [] }) => {
  const isEdit = !!initialData?.id;

  const [branchId, setBranchId] = useState(0);
  const [deliveryTypeId, setDeliveryTypeId] = useState(4);
  const [status, setStatus] = useState(1);

  const [barrierTimeBefore, setBarrierTimeBefore] = useState(60);
  const [barrierTimeAfter, setBarrierTimeAfter] = useState(60);

  const [title, setTitle] = useState("");
  const [standardDeliveryFees, setStandardDeliveryFees] = useState([DEFAULT_FEE_ROW()]);
  const [isSaving, setIsSaving] = useState(false);

  const [errors, setErrors] = useState({
    title: false,
    branchId: false,
    standardDeliveryFees: [],
    common: null,
  });

  const branchesOptions = useMemo(() => {
    return [{ branchId: 0, branchName: "All" }, ...(Array.isArray(allBranches) ? allBranches : [])];
  }, [allBranches]);

  useEffect(() => {
    if (!show) return;

    if (initialData) {
      setBranchId(toInt(initialData.branch_id, 0));
      setDeliveryTypeId(toInt(initialData.delivery_type_id, 4));
      setStatus(toInt(initialData.status, 1));
      setBarrierTimeBefore(toInt(initialData.barrier_time_before, 60));
      setBarrierTimeAfter(toInt(initialData.barrier_time_after, 60));
      setTitle(initialData.title || "");

      const fees =
        Array.isArray(initialData.StandardDeliveryFees) && initialData.StandardDeliveryFees.length > 0
          ? initialData.StandardDeliveryFees.map((f) => ({
            id: f.id,
            min_order_value: normalizeNumberString(f.min_order_value),
            max_order_value: normalizeNumberString(f.max_order_value),
            min_radius_km: normalizeNumberString(f.min_radius_km ?? 0),
            max_radius_km: normalizeNumberString(f.max_radius_km),
            fee: normalizeNumberString(f.fee),
            smart_cart_fee: normalizeNumberString(f.smart_cart_fee ?? 0),
            threshold: normalizeNumberString(f.threshold),
            status: toInt(f.status, 1),
          }))
          : [DEFAULT_FEE_ROW()];

      setStandardDeliveryFees(fees);
    } else {
      setBranchId(0);
      setDeliveryTypeId(4);
      setStatus(1);
      setBarrierTimeBefore(60);
      setBarrierTimeAfter(60);
      setTitle("");
      setStandardDeliveryFees([DEFAULT_FEE_ROW()]);
    }

    setErrors({ title: false, branchId: false, standardDeliveryFees: [], common: null });
    setIsSaving(false);
  }, [initialData, show]);

  const validateForm = () => {
    let ok = true;

    const newErrors = {
      title: !String(title || "").trim(),
      branchId: branchId === null || branchId === undefined || branchId === "",
      standardDeliveryFees: [],
      common: null,
    };

    if (newErrors.title || newErrors.branchId) ok = false;

    if (!standardDeliveryFees || standardDeliveryFees.length === 0) {
      ok = false;
      newErrors.common = "At least one fee slab is required.";
      setErrors(newErrors);
      return false;
    }

    for (let i = 0; i < standardDeliveryFees.length; i++) {
      const fee = standardDeliveryFees[i] || {};
      const e = {};

      const required = (v) => v === "" || v === null || v === undefined;

      if (required(fee.min_order_value)) e.min_order_value = true;
      if (required(fee.max_order_value)) e.max_order_value = true;
      if (required(fee.min_radius_km)) e.min_radius_km = true;
      if (required(fee.max_radius_km)) e.max_radius_km = true;
      if (required(fee.fee)) e.fee = true;
      if (required(fee.threshold)) e.threshold = true;

      const minO = toFloat(fee.min_order_value, -1);
      const maxO = toFloat(fee.max_order_value, -1);
      if (!e.min_order_value && minO < 0) e.min_order_value = true;
      if (!e.max_order_value && maxO < 0) e.max_order_value = true;
      if (!e.min_order_value && !e.max_order_value && maxO !== 0 && maxO < minO) {
        e.max_order_value = true;
        e._msg = "Max Order should be >= Min Order (or 0 for no limit).";
      }

      const minR = toFloat(fee.min_radius_km, -1);
      const maxR = toFloat(fee.max_radius_km, -1);
      if (!e.min_radius_km && minR < 0) e.min_radius_km = true;
      if (!e.max_radius_km && maxR <= 0) e.max_radius_km = true;
      if (!e.min_radius_km && !e.max_radius_km && maxR < minR) {
        e.max_radius_km = true;
        e._msg = "Max Radius should be >= Min Radius.";
      }

      newErrors.standardDeliveryFees[i] = e;
      if (Object.keys(e).length > 0) ok = false;
    }

    setErrors(newErrors);
    return ok;
  };

  const handleStandardFeeChange = (index, field, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      const updated = [...standardDeliveryFees];
      updated[index] = { ...updated[index], [field]: value };
      setStandardDeliveryFees(updated);
    }
  };

  const handleAddFee = () => setStandardDeliveryFees((p) => [...p, DEFAULT_FEE_ROW()]);
  const handleRemoveFee = (index) => {
    setStandardDeliveryFees((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== index)));
  };

  const buildPayload = () => ({
    branch_id: toInt(branchId, 0),
    delivery_type_id: toInt(deliveryTypeId, 4),
    status: toInt(status, 1),
    barrier_time_before: toInt(barrierTimeBefore, 0),
    barrier_time_after: toInt(barrierTimeAfter, 0),
    title: String(title || "").trim(),
    standard_delivery_fees: standardDeliveryFees.map((f) => ({
      ...(f.id ? { id: f.id } : {}),
      min_order_value: toFloat(f.min_order_value, 0),
      max_order_value: toFloat(f.max_order_value, 0),
      min_radius_km: toFloat(f.min_radius_km, 0),
      max_radius_km: toFloat(f.max_radius_km, 0),
      fee: toFloat(f.fee, 0),
      smart_cart_fee: toFloat(f.smart_cart_fee, 0),
      threshold: toInt(f.threshold, 0),
      status: toInt(f.status, 1),
    })),
  });

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSaving(true);
      const payload = buildPayload();

      const resp = isEdit
        ? await deliveryApi.updateDeliveryOption(initialData.id, payload)
        : await deliveryApi.addDeliveryOption(payload);

      const ok = resp?.status === "SUCCESS" || resp?.success === true;
      const msg = resp?.message || (ok ? "Saved successfully" : "Save failed");

      // ✅ KEY FIX: Close modal first, then show confirm (no await)
      if (ok) {
        setIsSaving(false);
        onHide?.();
        showConfirm("Success", { description: msg, type: "Success", hideCancel: true }).then(() => {
          onSave?.();
        });
      } else {
        setIsSaving(false);
        showConfirm("Oops!", { description: msg, type: "warning", hideCancel: true });
      }
    } catch (err) {
      console.error(err);
      setIsSaving(false);
      showConfirm("Oops!", {
        description: err?.message || "Save failed, kindly try again later!",
        type: "warning",
        hideCancel: true,
      });
    }
  };

  const onClose = () => {
    if (isSaving) return;
    onHide?.();
  };

  return (
    <ModalContainer show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit Delivery Option" : "Add Delivery Option"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Branch</Form.Label>
                <Form.Control
                  as="select"
                  value={branchId}
                  onChange={(e) => setBranchId(toInt(e.target.value, 0))}
                  className={`modern-input ${errors.branchId ? "is-invalid" : ""}`}
                  disabled={isSaving}
                >
                  <option value="">Select a Branch</option>
                  {branchesOptions.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchName}</option>
                  ))}
                </Form.Control>
                {errors.branchId && <div className="invalid-feedback">Please select a branch.</div>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Delivery Type</Form.Label>
                <Form.Control
                  as="select"
                  value={deliveryTypeId}
                  onChange={(e) => setDeliveryTypeId(toInt(e.target.value, 4))}
                  className="modern-input"
                  disabled={isSaving}
                >
                  <option value={4}>Standard Delivery</option>
                  <option value={2}>Express Delivery</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Title</Form.Label>
                <Form.Control
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={`modern-input ${errors.title ? "is-invalid" : ""}`}
                  disabled={isSaving}
                />
                {errors.title && <div className="invalid-feedback">Title is required.</div>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Status</Form.Label>
                <Form.Control
                  as="select"
                  value={status}
                  onChange={(e) => setStatus(toInt(e.target.value, 1))}
                  className="modern-input"
                  disabled={isSaving}
                >
                  <option value={1}>Active</option>
                  <option value={2}>Inactive</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Barrier Time Before</Form.Label>
                <Form.Control
                  type="number"
                  value={barrierTimeBefore}
                  onChange={(e) => setBarrierTimeBefore(toInt(e.target.value, 0))}
                  className="modern-input"
                  disabled={isSaving}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Barrier Time After</Form.Label>
                <Form.Control
                  type="number"
                  value={barrierTimeAfter}
                  onChange={(e) => setBarrierTimeAfter(toInt(e.target.value, 0))}
                  className="modern-input"
                  disabled={isSaving}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          <div className="fee-section">
            <h5 className="fw-bold text-primary">Delivery Fees (Order Value + Radius)</h5>

            {standardDeliveryFees.map((fee, index) => {
              const e = errors.standardDeliveryFees?.[index] || {};
              return (
                <div key={fee.id || index} className="fee-entry">
                  <MdRemoveCircle
                    className={`remove-icon ${standardDeliveryFees.length === 1 ? "disabled" : ""}`}
                    onClick={() => handleRemoveFee(index)}
                  />

                  {e._msg && <div className="alert alert-warning py-1 mb-2">{e._msg}</div>}

                  <Row className="g-3">
                    <Col md={2}>
                      <Form.Label>Min Order Value</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.min_order_value}
                        onChange={(ev) => handleStandardFeeChange(index, "min_order_value", ev.target.value)}
                        className={`modern-input ${e.min_order_value ? "is-invalid" : ""}`}
                        disabled={isSaving}
                      />
                    </Col>

                    <Col md={2}>
                      <Form.Label>Max Order Value</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.max_order_value}
                        onChange={(ev) => handleStandardFeeChange(index, "max_order_value", ev.target.value)}
                        className={`modern-input ${e.max_order_value ? "is-invalid" : ""}`}
                        disabled={isSaving}
                      />
                    </Col>

                    <Col md={2}>
                      <Form.Label>Min Radius</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.min_radius_km}
                        onChange={(ev) => handleStandardFeeChange(index, "min_radius_km", ev.target.value)}
                        className={`modern-input ${e.min_radius_km ? "is-invalid" : ""}`}
                        disabled={isSaving}
                      />
                    </Col>

                    <Col md={2}>
                      <Form.Label>Max Radius</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.max_radius_km}
                        onChange={(ev) => handleStandardFeeChange(index, "max_radius_km", ev.target.value)}
                        className={`modern-input ${e.max_radius_km ? "is-invalid" : ""}`}
                        disabled={isSaving}
                      />
                    </Col>

                    <Col md={2}>
                      <Form.Label>Fee</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.fee}
                        onChange={(ev) => handleStandardFeeChange(index, "fee", ev.target.value)}
                        className={`modern-input ${e.fee ? "is-invalid" : ""}`}
                        disabled={isSaving}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Smart Cart Fee</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.smart_cart_fee}
                        onChange={(ev) => handleStandardFeeChange(index, "smart_cart_fee", ev.target.value)}
                        className="modern-input"
                        disabled={isSaving}
                      />
                    </Col>
                  </Row>

                  <Row className="g-3 mt-2">
                    <Col md={3}>
                      <Form.Label>Threshold</Form.Label>
                      <Form.Control
                        type="number"
                        value={fee.threshold}
                        onChange={(ev) => handleStandardFeeChange(index, "threshold", ev.target.value)}
                        className={`modern-input ${e.threshold ? "is-invalid" : ""}`}
                        disabled={isSaving}
                      />
                    </Col>

                    <Col md={3}>
                      <Form.Label>Status</Form.Label>
                      <Form.Control
                        as="select"
                        value={toInt(fee.status, 1)}
                        onChange={(ev) => {
                          const updated = [...standardDeliveryFees];
                          updated[index] = { ...updated[index], status: toInt(ev.target.value, 1) };
                          setStandardDeliveryFees(updated);
                        }}
                        className="modern-input"
                        disabled={isSaving}
                      >
                        <option value={1}>Active</option>
                        <option value={2}>Inactive</option>
                      </Form.Control>
                    </Col>

                    <Col md={6} className="d-flex align-items-end justify-content-end">
                      <div className="hint">Slab #{index + 1}</div>
                    </Col>
                  </Row>
                </div>
              );
            })}

            <Button variant="primary" onClick={handleAddFee} className="add-fee-btn" disabled={isSaving}>
              Add Fee Slab
            </Button>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} className="cancel-btn" disabled={isSaving}>
          Close
        </Button>
        <Button variant="success" onClick={handleSubmit} className="save-btn" disabled={isSaving}>
          {isSaving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </ModalContainer>
  );
};

export default DeliveryOptionForm;